/* ==========================================================================
   SC Product Gallery (CS-6 / F-09)

   - Thumbnail-Slider außerhalb des Hauptbildes (Swiper "thumbs")
   - Zoom auf dem Hauptbild: Hover auf Desktop, Pinch & Doppeltipp auf Touch,
     Lupen-Button auf beiden
   - Swipe zwischen den Bildern auf Mobile
   - Bilder der Nachbar-Slides werden erst bei Bedarf nachgeladen (lazy loading)

   Exponiert `scGoToMedia(mediaId)` am Galerie-Element – wird vom
   Variantenwähler in custom.js genutzt.
   ========================================================================== */

(function () {
  'use strict';

  var FINE_POINTER = '(hover: hover) and (pointer: fine)';

  function ScProductGallery(root) {
    this.root = root;
    this.stage = root.querySelector('[data-sc-gallery-main]');
    this.thumbsEl = root.querySelector('[data-sc-gallery-thumbs]');
    this.counterCurrent = root.querySelector('[data-sc-gallery-current]');
    this.zoomToggle = root.querySelector('[data-sc-gallery-zoom-toggle]');
    this.zoomLevel = parseFloat(root.dataset.scZoomLevel) || 2.2;
    this.zoomEnabled = root.dataset.scZoom !== 'false';
    this.pointerQuery = window.matchMedia(FINE_POINTER);
    this.zoomLocked = false;

    this.onThumbClick = this.onThumbClick.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onZoomToggle = this.onZoomToggle.bind(this);
    this.onPointerQueryChange = this.onPointerQueryChange.bind(this);
  }

  ScProductGallery.prototype.init = function () {
    if (!this.stage || typeof window.Swiper !== 'function') return false;

    var self = this;
    var slideCount = this.stage.querySelectorAll('.swiper-slide').length;
    var isFinePointer = this.pointerQuery.matches;

    if (this.thumbsEl && slideCount > 1) {
      this.thumbs = new window.Swiper(this.thumbsEl, {
        slidesPerView: 'auto',
        spaceBetween: 10,
        freeMode: true,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        watchOverflow: true,
      });
    }

    var params = {
      speed: 420,
      watchOverflow: true,
      grabCursor: !isFinePointer,
      // Auf dem Desktop wird per Pfeil/Thumbnail navigiert – Maus-Drag würde
      // sonst mit dem Hover-Zoom kollidieren.
      simulateTouch: !isFinePointer,
      touchReleaseOnEdges: true,
      threshold: 5,
      keyboard: { enabled: true, onlyInViewport: true },
      navigation: {
        prevEl: this.root.querySelector('[data-sc-gallery-prev]'),
        nextEl: this.root.querySelector('[data-sc-gallery-next]'),
      },
      zoom: {
        enabled: this.zoomEnabled,
        maxRatio: Math.max(this.zoomLevel, 2),
        toggle: true,
      },
      on: {
        init: function (swiper) {
          self.preloadAround(swiper.activeIndex, swiper);
          self.updateCounter(swiper.activeIndex);
          self.updateThumbState(swiper.activeIndex);
        },
        slideChange: function (swiper) {
          self.preloadAround(swiper.activeIndex, swiper);
          self.updateCounter(swiper.activeIndex);
          self.updateThumbState(swiper.activeIndex);
          self.resetZoom(swiper);
        },
        // Pinch/Doppeltipp halten den Button-Zustand aktuell
        zoomChange: function (swiper, scale) {
          self.setZoomPressed(scale > 1);
        },
      },
    };

    if (this.thumbs) {
      params.thumbs = { swiper: this.thumbs, autoScrollOffset: 1 };
    }

    this.slider = new window.Swiper(this.stage, params);

    // Swiper-Zoom (Pinch/Doppeltipp) nur auf Touch – auf dem Desktop
    // übernimmt der Hover-Zoom, sonst würden sich beide Transforms überlagern.
    if (this.zoomEnabled && this.slider.zoom) {
      if (isFinePointer) {
        this.slider.zoom.disable();
      } else {
        this.slider.zoom.enable();
      }
    }

    if (this.thumbsEl) {
      this.bindThumbs();
    }

    if (this.zoomEnabled) {
      this.root.classList.add('sc-gallery--zoomable');
      this.root.style.setProperty('--sc-gallery-zoom', String(this.zoomLevel));
      this.bindZoomControls();
    }

    this.root.scSlider = this.slider;
    this.root.scSliderThumbnail = this.thumbs;
    this.root.scGoToMedia = function (mediaId) {
      self.goToMedia(mediaId);
    };

    this.syncVariantPicker();
    return true;
  };

  /* --- Navigation ---------------------------------------------------------- */

  ScProductGallery.prototype.goToMedia = function (mediaId) {
    if (!mediaId || !this.slider) return;

    var slides = this.stage.querySelectorAll('.swiper-slide');
    var target = String(mediaId);
    var index = -1;

    for (var i = 0; i < slides.length; i++) {
      if (String(slides[i].dataset.scMediaId) === target) {
        index = i;
        break;
      }
    }

    if (index < 0) return;
    this.slider.slideTo(index);
  };

  ScProductGallery.prototype.syncVariantPicker = function () {
    var picker = document.querySelector('sc-variant-picker');

    if (picker && typeof picker.syncGallery === 'function') {
      picker.gallery = this.root;
      picker.syncGallery();
    }
  };

  // Swipers thumbs-Modul haengt ausschliesslich an seinem eigenen tap-Event,
  // das nur aus einem Pointer-Zyklus entsteht. Enter/Space auf dem Button feuert
  // ein natives click ohne Pointer-Zyklus – der Bildwechsel per Tastatur braucht
  // deshalb einen eigenen Handler.
  ScProductGallery.prototype.bindThumbs = function () {
    this.thumbsEl.addEventListener('click', this.onThumbClick);
  };

  ScProductGallery.prototype.onThumbClick = function (event) {
    var slide = event.target.closest ? event.target.closest('.swiper-slide') : null;
    if (!slide) return;

    this.goToMedia(slide.dataset.scMediaId);
  };

  ScProductGallery.prototype.updateThumbState = function (index) {
    if (!this.thumbsEl) return;

    var thumbs = this.thumbsEl.querySelectorAll('.sc-gallery__thumb');

    for (var i = 0; i < thumbs.length; i++) {
      if (i === index) {
        thumbs[i].setAttribute('aria-current', 'true');
      } else {
        thumbs[i].removeAttribute('aria-current');
      }
    }
  };

  ScProductGallery.prototype.updateCounter = function (index) {
    if (!this.counterCurrent) return;
    this.counterCurrent.textContent = String(index + 1);
  };

  /* --- Lazy Loading -------------------------------------------------------- */

  // Slides außerhalb des Viewports werden vom Browser nicht geladen. Damit beim
  // Wischen kein leerer Slide erscheint, werden die direkten Nachbarn vorgezogen.
  ScProductGallery.prototype.preloadAround = function (index, swiper) {
    var instance = swiper || this.slider;
    if (!instance) return;

    var slides = instance.slides;

    for (var i = index - 1; i <= index + 1; i++) {
      if (i < 0 || !slides[i]) continue;

      var image = slides[i].querySelector('img[loading="lazy"]');
      if (image) image.setAttribute('loading', 'eager');
    }
  };

  /* --- Zoom (Desktop) ------------------------------------------------------ */

  ScProductGallery.prototype.bindZoomControls = function () {
    this.stage.addEventListener('pointermove', this.onPointerMove);
    this.stage.addEventListener('pointerleave', this.onPointerLeave);

    if (this.zoomToggle) {
      this.zoomToggle.addEventListener('click', this.onZoomToggle);
    }

    if (typeof this.pointerQuery.addEventListener === 'function') {
      this.pointerQuery.addEventListener('change', this.onPointerQueryChange);
    }
  };

  ScProductGallery.prototype.activeZoomEl = function () {
    if (!this.slider || !this.slider.slides) return null;
    var slide = this.slider.slides[this.slider.activeIndex];
    return slide ? slide.querySelector('.sc-gallery__zoom') : null;
  };

  ScProductGallery.prototype.setZoomPressed = function (pressed) {
    if (this.zoomToggle) {
      this.zoomToggle.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    }
  };

  // Setzt den Zoom auf allen Slides zurueck – beim Slidewechsel kann der
  // zuletzt gezoomte Slide nicht mehr der aktive sein.
  ScProductGallery.prototype.resetZoom = function (swiper) {
    this.zoomLocked = false;
    this.setZoomPressed(false);

    this.root.querySelectorAll('.sc-gallery__zoom.is-zoomed').forEach(function (el) {
      el.classList.remove('is-zoomed');
      var image = el.querySelector('.sc-gallery__image');
      if (image) image.style.transformOrigin = '';
    });

    // Touch: Swipers eigenen Zoom-Zustand mit zurücksetzen
    var instance = swiper || this.slider;
    if (instance && instance.zoom && instance.zoom.scale !== 1) {
      instance.zoom.out();
    }
  };

  ScProductGallery.prototype.onPointerMove = function (event) {
    if (!this.pointerQuery.matches || event.pointerType === 'touch') return;

    var el = event.target.closest ? event.target.closest('.sc-gallery__zoom') : null;
    if (!el) return;

    var image = el.querySelector('.sc-gallery__image');
    if (!image) return;

    el.classList.add('is-zoomed');

    var rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    var x = ((event.clientX - rect.left) / rect.width) * 100;
    var y = ((event.clientY - rect.top) / rect.height) * 100;

    image.style.transformOrigin =
      Math.min(Math.max(x, 0), 100) + '% ' + Math.min(Math.max(y, 0), 100) + '%';
  };

  ScProductGallery.prototype.onPointerLeave = function (event) {
    if (this.zoomLocked || (event && event.pointerType === 'touch')) return;
    this.resetZoom();
  };

  // Auf Touch der einzige sichtbare Zugang zum Zoom, auf Desktop der
  // Tastatur-/Touchpad-Zugang ohne Hover.
  ScProductGallery.prototype.onZoomToggle = function () {
    // Touch: denselben Zustand steuern wie Pinch und Doppeltipp. aria-pressed
    // setzt daraufhin der zoomChange-Handler.
    if (!this.pointerQuery.matches) {
      if (this.slider && this.slider.zoom) this.slider.zoom.toggle();
      return;
    }

    var el = this.activeZoomEl();
    if (!el) return;

    this.zoomLocked = !this.zoomLocked;
    el.classList.toggle('is-zoomed', this.zoomLocked);

    if (!this.zoomLocked) {
      var image = el.querySelector('.sc-gallery__image');
      if (image) image.style.transformOrigin = '';
    }

    this.setZoomPressed(this.zoomLocked);
  };

  // Swipers zoom.out() hinterlaesst transform und transitionDuration inline auf
  // Wrap und Bild. Inline schlaegt die Stylesheet-Regel des Hover-Zooms – ohne
  // dieses Aufraeumen bleibt der Desktop-Zoom auf genau den zuvor per Pinch
  // gezoomten Bildern tot. Muss nach resetZoom()/zoom.out() laufen, sonst
  // schreibt Swiper die Werte direkt wieder hinein.
  ScProductGallery.prototype.clearSwiperZoomStyles = function () {
    this.root.querySelectorAll('.sc-gallery__zoom').forEach(function (el) {
      el.style.transform = '';
      el.style.transitionDuration = '';

      var image = el.querySelector('.sc-gallery__image');
      if (image) {
        image.style.transform = '';
        image.style.transitionDuration = '';
      }
    });
  };

  ScProductGallery.prototype.onPointerQueryChange = function (event) {
    this.resetZoom();

    if (!this.slider) return;

    if (this.slider.zoom) {
      if (event.matches) {
        this.slider.zoom.disable();
        this.clearSwiperZoomStyles();
      } else {
        this.slider.zoom.enable();
      }
    }

    this.slider.params.simulateTouch = !event.matches;
    this.slider.params.grabCursor = !event.matches;
  };

  ScProductGallery.prototype.destroy = function () {
    if (this.slider) this.slider.destroy(true, true);
    if (this.thumbs) this.thumbs.destroy(true, true);

    if (this.thumbsEl) {
      this.thumbsEl.removeEventListener('click', this.onThumbClick);
    }

    if (this.stage) {
      this.stage.removeEventListener('pointermove', this.onPointerMove);
      this.stage.removeEventListener('pointerleave', this.onPointerLeave);
    }

    if (this.zoomToggle) {
      this.zoomToggle.removeEventListener('click', this.onZoomToggle);
    }

    if (typeof this.pointerQuery.removeEventListener === 'function') {
      this.pointerQuery.removeEventListener('change', this.onPointerQueryChange);
    }

    delete this.root.scGoToMedia;
    delete this.root.scSlider;
    delete this.root.scSliderThumbnail;
    delete this.root.scGalleryInstance;
  };

  /* --- Bootstrapping -------------------------------------------------------- */

  function mount(root) {
    if (!root || root.scGalleryInstance) return;

    var gallery = new ScProductGallery(root);

    if (gallery.init()) {
      root.scGalleryInstance = gallery;
      return;
    }

    // swiper.js ist noch nicht ausgeführt – auf das Laden warten.
    if (typeof window.Swiper !== 'function') {
      window.addEventListener('load', function () {
        if (!root.scGalleryInstance && gallery.init()) {
          root.scGalleryInstance = gallery;
        }
      }, { once: true });
    }
  }

  function mountAll(scope) {
    (scope || document).querySelectorAll('[data-sc-product-gallery]').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      mountAll();
    });
  } else {
    mountAll();
  }

  // Theme-Editor: Section wird neu gerendert
  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-sc-product-gallery]').forEach(function (root) {
      if (root.scGalleryInstance) root.scGalleryInstance.destroy();
    });
  });
})();
